<script lang="ts">
  import { addRoute } from '@integro/core';
  import { gettext, pgettext, ngettext, npgettext } from '@integro/gettext';
  addRoute({
    name: 'products',
    path: '/products',
    component: 'ProductBrowser',
    title: () => pgettext('ProductsView', 'Products'),
  });
</script>

<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'ProductBrowser',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const grid = ref(null);

  const $refs = {
    get grid() {
      return grid.value;
    },
  };

  const selectedItems = ref([]);
  const ds = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'sm',
  }));
  const b_2248226175642056_columnDefaults = computed(() => ({
    sortable: true,
    filterable: true,
  }));
  const b_2248226175642056_columns = computed(() => [
    {
      field: 'number',
      name: $fn.value.pgettext('ProductsView', 'Code'),
      type: 'command',
      formatter: {
        name: 'routeLink',
        to: 'product',
        params: {
          id: 'id',
        },
      },
    },
    {
      field: 'name',
      name: $fn.value.pgettext('ProductsView', 'Name'),
      type: 'command',
      formatter: {
        name: 'routeLink',
        to: 'product',
        params: {
          id: 'id',
        },
      },
    },
    {
      field: 'product_type',
      name: $fn.value.pgettext('ProductsView', 'Product type'),
      formatter: {
        name: 'gettext',
        context: 'ProductTypes',
        conversion: 'StartCase',
        filterInput: true,
      },
    },
    {
      field: 'invoice_group',
      name: $fn.value.pgettext('ProductsView', 'Invoice group'),
    },
    {
      field: 'unit',
      name: $fn.value.pgettext('ProductsView', 'Unit'),
    },
    {
      field: 'sales_vat_code',
      name: $fn.value.pgettext('ProductsView', 'Sales VAT code'),
    },
    {
      field: 'purchase_vat_code',
      name: $fn.value.pgettext('ProductsView', 'Purchase VAT code'),
    },
    {
      field: 'groups',
      name: $fn.value.pgettext('ProductsView', 'Groups'),
    },
    {
      field: 'external_key',
      name: $fn.value.pgettext('ProductsView', 'External key'),
    },
  ]);

  const $data = computed(() => ({
    selectedItems: selectedItems.value,
    ds: ds.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
    b_2248226175642056_columnDefaults: b_2248226175642056_columnDefaults.value,
    b_2248226175642056_columns: b_2248226175642056_columns.value,
  }));

  const $$load_ds = () => {
    return $fn.value.gridDataSource('list_product');
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'selectedItems' },
      { name: 'ds' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
      { name: 'b_2248226175642056_columnDefaults', nodeId: '2248226175642056' },
      { name: 'b_2248226175642056_columns', nodeId: '2248226175642056' },
    ],
    loaders: { ds: $$load_ds },
    ux: {},
    dataRefs: {
      selectedItems,
      ds,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
      b_2248226175642056_columnDefaults,
      b_2248226175642056_columns,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return {
      title: $fn.value.pgettext('ProductsView', 'Products'),
      icon: $config.value.product.icon,
    };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_ci82x8x1v_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_2248226175642056_setData_r4wqwiuti_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'selectedItems',
      value: $refs.grid.getSelectedItems(),
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_4615685607825208_showModal_y53qocffk_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'CreateProduct',
    };
  };

  const act_7315092382398562_closeModal_ci82x8x1v =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_ci82x8x1v_getActionArgs,
    });
  const inst_evh_7315092382398562_close_5n2ay9kdl =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_ci82x8x1v,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_5n2ay9kdl],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_lf4t6opty =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_8nnqme31o =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_lf4t6opty,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_8nnqme31o],
    event: 'reload',
  });
  const act_2248226175642056_setData_r4wqwiuti = new actionTypes.SetDataAction({
    actionArgs: act_2248226175642056_setData_r4wqwiuti_getActionArgs,
  });
  const inst_evh_2248226175642056_selectedRowsChanged_z9ltoz50e =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_setData_r4wqwiuti,
      event: 'selectedRowsChanged',
      displayName: 'setData',
    });
  const inst_evh_2248226175642056_selectedRowsChanged =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2248226175642056_selectedRowsChanged_z9ltoz50e],
      event: 'selectedRowsChanged',
    });
  const act_4615685607825208_showModal_y53qocffk =
    new actionTypes.ShowModalAction({
      actionArgs: act_4615685607825208_showModal_y53qocffk_getActionArgs,
    });
  const inst_evh_4615685607825208_click_hvwduyu8r =
    new actionTypes.EventHandlerImpl({
      action: act_4615685607825208_showModal_y53qocffk,
      event: 'click',
      displayName: 'showModal',
    });
  const inst_evh_4615685607825208_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_4615685607825208_click_hvwduyu8r],
    event: 'click',
  });

  const actions = {
    inst_evh_7315092382398562_close_5n2ay9kdl,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_8nnqme31o,
    inst_evh_7315092382398562_reload,
    inst_evh_2248226175642056_selectedRowsChanged_z9ltoz50e,
    inst_evh_2248226175642056_selectedRowsChanged,
    inst_evh_4615685607825208_click_hvwduyu8r,
    inst_evh_4615685607825208_click,
  };

  const evh_7315092382398562_close_5n2ay9kdl = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_5n2ay9kdl.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_8nnqme31o = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_8nnqme31o.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_selectedRowsChanged_z9ltoz50e = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2248226175642056_selectedRowsChanged_z9ltoz50e.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_selectedRowsChanged = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_selectedRowsChanged.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4615685607825208_click_hvwduyu8r = async (event, scope) => {
    return await actions.inst_evh_4615685607825208_click_hvwduyu8r.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4615685607825208_click = async (event, scope) => {
    return await actions.inst_evh_4615685607825208_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="ProductBrowser"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <ItpDataGrid
      name="products"
      :headerTitle="pgettext('ProductsView', 'Products')"
      :items="ds"
      checkbox-row-selection
      export-to-csv
      :exportToCsvFilename="pgettext('ExportFilename', 'products')"
      :columnDefaults="b_2248226175642056_columnDefaults"
      :columns="b_2248226175642056_columns"
      class="page--grid"
      ref="grid"
      @selectedRowsChanged="
        evh_2248226175642056_selectedRowsChanged($event, {})
      "
    >
      <template #toolbar>
        <div class="flex-grow-1 d-flex gap-2">
          <ItpButton
            :icon="$config.product.addProductIcon"
            :text="pgettext('ProductsView', 'New Product/Service...')"
            size="sm"
            variant="light"
            @click="evh_4615685607825208_click($event, {})"
          >
          </ItpButton>
          <PageHelp path="/products/products.html"> </PageHelp>
        </div>
      </template>
    </ItpDataGrid>
  </ItpPage>
</template>
